import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { injectEnvironment } from '@cf/core';
import { InputErrorComponent, LabelComponent } from '@cf/shared/forms/form-field';
import { CfIconDirective } from '@cf/shared/ui/icons';
import { TextComponent } from '@cf/shared/ui/typography';
import { CfSchemaMeetingInfo, EPricingModel } from '@cf/temp/services';
import { StripeCardElementOptions, StripeElementsOptions } from '@stripe/stripe-js';
import { PaymentMethodResult } from '@stripe/stripe-js/dist/stripe-js/stripe';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { injectStripe, StripeCardComponent, StripeElementsDirective } from 'ngx-stripe';
import { Observable } from 'rxjs';
import { BookingPaymentStateService } from '../../services/booking-payment-state.service';

@Component({
  selector: 'cf-booking-page-payment',
  imports: [
    NgxTolgeeModule,
    TextComponent,
    LabelComponent,
    StripeElementsDirective,
    StripeCardComponent,
    InputErrorComponent,
    CfIconDirective,
  ],
  templateUrl: './booking-page-payment.component.html',
  styleUrl: './booking-page-payment.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingPagePaymentComponent implements OnInit {
  @ViewChild(StripeCardComponent) cardElement!: StripeCardComponent;

  @Input({ required: true }) booking!: CfSchemaMeetingInfo;
  @Input({ required: true }) guestCount!: number;
  @Input() duration!: string;

  private readonly bookingPaymentStateService = inject(BookingPaymentStateService);

  stripeAccountConnected = this.bookingPaymentStateService.stripeAccountConnected;

  errorMsg!: string | undefined;

  private environment = injectEnvironment();

  stripe = injectStripe(this.environment.stripeId);
  cd = inject(ChangeDetectorRef);

  EPricingModel = EPricingModel;

  cardOptions: StripeCardElementOptions = {
    hidePostalCode: true,
    style: {
      base: {
        iconColor: '#ACB0B9',
        color: '#333333',
        fontSize: '14px',
        lineHeight: '22px',
        '::placeholder': {
          color: '#ACB0B9',
          textTransform: 'capitalize',
        },
      },
    },
  };

  elementsOptions: StripeElementsOptions = {
    locale: 'en',
  };

  get totalPrice() {
    if (!this.booking.price) {
      return '0.00';
    }

    if (this.booking.pricing_model === EPricingModel.HOURLY) {
      return (+this.booking.price * (+this.duration / 60)).toFixed(2);
    } else if (this.booking.pricing_model === EPricingModel.PER_ATTENDEE) {
      return ((this.guestCount + this.booking.members_amount) * +this.booking.price).toFixed(2);
    } else {
      return (+this.booking.price).toFixed(2);
    }
  }

  get bookingTranslateObj() {
    return {
      price: this.booking.price,
      currency_code: this.booking.currency_code,
      total_price: this.totalPrice,
    };
  }

  ngOnInit() {
    if (this.booking.stripe_account_connected !== undefined) {
      this.bookingPaymentStateService.stripeAccountConnected.set(
        this.booking.stripe_account_connected ?? false,
      );
    }
  }

  createToken(): Observable<PaymentMethodResult> {
    return this.stripe.createPaymentMethod({
      card: this.cardElement.element,
      type: 'card',
    });
  }
}
